/**
 * Country table for the phone capture field.
 *
 * The ONE place to manage dial codes, flags and number lengths. To add/adjust a
 * country, edit a row here — the flag selector, the max-length cap on the input,
 * and validation all read from this. `nsn` is the national significant number
 * length (digits AFTER the dial code) as [min, max].
 *
 * Flags are emoji (regional-indicator pairs) so there are no image assets; they
 * render natively on macOS/iOS/Android. Windows falls back to the ISO letters.
 */
export interface Country {
  iso: string; // ISO-3166 alpha-2, e.g. "CL"
  name: string;
  dial: string; // E.164 calling code with leading "+", e.g. "+56"
  flag: string; // emoji flag
  nsn: [number, number]; // national number length [min, max] in digits
}

/** Fallback when no default resolves — also the first row in the list. */
export const FALLBACK_COUNTRY: Country = {
  iso: "CL",
  name: "Chile",
  dial: "+56",
  flag: "🇨🇱",
  nsn: [9, 9],
};

export const COUNTRIES: Country[] = [
  FALLBACK_COUNTRY,
  { iso: "MX", name: "México", dial: "+52", flag: "🇲🇽", nsn: [10, 10] },
  { iso: "AR", name: "Argentina", dial: "+54", flag: "🇦🇷", nsn: [10, 11] },
  { iso: "CO", name: "Colombia", dial: "+57", flag: "🇨🇴", nsn: [10, 10] },
  { iso: "PE", name: "Perú", dial: "+51", flag: "🇵🇪", nsn: [9, 9] },
  { iso: "BR", name: "Brasil", dial: "+55", flag: "🇧🇷", nsn: [10, 11] },
  { iso: "EC", name: "Ecuador", dial: "+593", flag: "🇪🇨", nsn: [8, 9] },
  { iso: "UY", name: "Uruguay", dial: "+598", flag: "🇺🇾", nsn: [8, 8] },
  { iso: "US", name: "United States", dial: "+1", flag: "🇺🇸", nsn: [10, 10] },
  { iso: "ES", name: "España", dial: "+34", flag: "🇪🇸", nsn: [9, 9] },
  { iso: "GB", name: "United Kingdom", dial: "+44", flag: "🇬🇧", nsn: [10, 10] },
];

/** Longest national number across all rows — used as a safe input maxlength fallback. */
export function maxNsn(c: Country): number {
  return c.nsn[1];
}

export function countryByIso(iso?: string): Country | undefined {
  if (!iso) return undefined;
  const up = iso.toUpperCase();
  return COUNTRIES.find((c) => c.iso === up);
}

/** First country matching a dial code ("+1" → US). */
export function countryByDial(dial?: string): Country | undefined {
  if (!dial) return undefined;
  const norm = dial.startsWith("+") ? dial : `+${dial.replace(/^\+?/, "")}`;
  return COUNTRIES.find((c) => c.dial === norm);
}

/**
 * Resolve the default country from a `phoneCountryCode` (a dial like "+56") or an
 * ISO code, falling back to the first row.
 */
export function resolveDefaultCountry(codeOrIso?: string): Country {
  const v = (codeOrIso || "").trim();
  return countryByDial(v) || countryByIso(v) || FALLBACK_COUNTRY;
}
